import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { message } from "antd";
import CustomInputField from "../../../../../../CustomHTMLElements/CustomInputField";
import CustomSelectDropdown from "../../../../../../CustomHTMLElements/CustomSelectDropdown";
import useBusinessTypes from "../../../../hooks/useBusinessTypes";
import useUserPositions from "../../../../hooks/useUserPositions";
import { postData } from "../../../../../../../newApis/dotNetApiMethods";
import { GetStartedSteps } from "../../types";
import {
  getMaxDateOfBirth,
  getTodaysDate,
} from "../../../../../../../helpers/validators";

import styles from "./VerifyBusiness.module.scss";

const schema = yup.object().shape({
  businessRegistrationNumber: yup.string().required("Please enter RC number"),
  businessName: yup.string().required("Business name is required"),
  customerSubType: yup
    .string()
    .required("Please select business type")
    .notOneOf([""], "Please select business type"),
  userPosition: yup
    .string()
    .required("Please select position")
    .notOneOf([""], "Please select position"),
  effectiveDate: yup.string().required("Please select effective date"),
});

type FormData = {
  businessRegistrationNumber: string;
  businessName: string;
  customerSubType: string;
  userPosition: string;
  effectiveDate: string;
  // password: string;
};

interface IVerifyBusiness {
  handleNextStep: (step: GetStartedSteps, data: any) => void;
  defaultValues?: Partial<any>;
}

const VerifyBusiness: React.FC<IVerifyBusiness> = ({
  handleNextStep,
  defaultValues = {},
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [passwordType, setPasswordType] = useState("password");
  const [isVerifying, setIsVerifying] = useState(false);
  const [businessVerificationData, setBusinessVerificationData] =
    useState<any>(null);

  const { register, handleSubmit, errors, setValue, watch } = useForm<FormData>(
    {
      resolver: yupResolver(schema),
      defaultValues: {
        businessRegistrationNumber:
          defaultValues.businessRegistrationNumber || "",
        businessName: defaultValues.businessName || "",
        customerSubType:
          defaultValues.customerSubType || defaultValues.business_type || "",
        userPosition: defaultValues.userPosition || "",
        effectiveDate: defaultValues.effectiveDate || "",
        // password: defaultValues.password || "",
      },
    },
  );

  const { data: businessTypes, isLoading } = useBusinessTypes();
  const { data: userPositions, isLoading: isLoadingUserPositions } =
    useUserPositions();

  // Pre-populate business type from initial selection if available
  useEffect(() => {
    if (defaultValues.business_type && !defaultValues.customerSubType) {
      setValue("customerSubType", defaultValues.business_type);
    }
  }, [defaultValues.business_type, defaultValues.customerSubType, setValue]);

  const togglePasswordField = () => {
    setPasswordType((prev) => (prev === "password" ? "text" : "password"));
  };

  const rcNumber = watch("businessRegistrationNumber");
  const selectedBusinessType = watch("customerSubType");

  const handleVerifyBusiness = async () => {
    if (!rcNumber || !selectedBusinessType) return;

    setIsVerifying(true);
    try {
      const payload = {
        businessRegistrationNumber: rcNumber,
        countryCode: "NG",
        businessType: Number(selectedBusinessType),
      };

      const endpoint = `/customer_verification/business`;
      const response = await postData(endpoint, payload);

      if (response?.data) {
        const {
          data: { fullName, address },
        } = response.data;

        message.success("Business name verified successfully");

        setValue("businessName", fullName);

        setBusinessVerificationData({
          businessAddress: address,
        });
      } else {
        const errorMsg = "Failed to verify business";
        message.error(errorMsg);
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message || "Failed to verify business";
      message.error(errorMsg);
    } finally {
      setIsVerifying(false);
    }
  };

  const onSubmit = (values: FormData) => {
    const submissionData = {
      ...values,
      ...businessVerificationData,
    };

    handleNextStep(GetStartedSteps.PREVIEW_BUSINESS, submissionData);
  };

  const handleProceedClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      className={styles.form}
    >
      <div className="row mt-4">
        <div className="col-12">
          <CustomSelectDropdown
            label="Business Type"
            name="customerSubType"
            reference={register}
            errors={errors?.customerSubType}
            isShowLabel={true}
            defaultValue={
              defaultValues.customerSubType || defaultValues.business_type || ""
            }
          >
            <option value="">Select business type</option>
            {isLoading && <option disabled>Loading business types...</option>}
            {businessTypes &&
              businessTypes?.data?.map((_businessType: any) => (
                <option key={_businessType?.id} value={_businessType?.id}>
                  {_businessType?.name}
                </option>
              ))}
          </CustomSelectDropdown>
        </div>

        <div className="col-12 mt-4">
          <div>
            <CustomInputField
              onBlur={handleVerifyBusiness}
              type="text"
              label="RC Number"
              name="businessRegistrationNumber"
              reference={register}
              placeholder="e.g., 12345678"
              errors={errors?.businessRegistrationNumber}
            />
          </div>
        </div>

        <div className="col-12">
          <CustomInputField
            type="text"
            label="Business Name"
            name="businessName"
            reference={register}
            placeholder="e.g., ABC Limited"
            errors={errors?.businessName}
            readOnly={true}
            loadingMessage={isVerifying ? "Searching for your business..." : ""}
          />
        </div>

        <div className="col-12 mt-n3">
          <CustomSelectDropdown
            label="Position"
            name="userPosition"
            reference={register}
            errors={errors?.userPosition}
            isShowLabel={true}
          >
            <option value="">Select position</option>
            {isLoadingUserPositions && (
              <option disabled>Loading positions...</option>
            )}
            {userPositions &&
              userPositions?.data?.map((position: any) => (
                <option key={position.id} value={position.id}>
                  {position.name}
                </option>
              ))}
          </CustomSelectDropdown>
        </div>

        <div className="col-12 mt-4">
          <CustomInputField
            type="date"
            label="Effective Date"
            name="effectiveDate"
            reference={register}
            errors={errors?.effectiveDate}
            max={getTodaysDate()}
          />
        </div>
      </div>

      <div className={styles.actionButtonContainer}>
        <button
          type="button"
          onClick={handleProceedClick}
          className="btn advancly-btn btn-lg btn-block"
        >
          Join Advancly
        </button>
      </div>
    </form>
  );
};

export default VerifyBusiness;
